// Main battery: nine 16"/50 rifles in three turrets. Real-ish exterior
// ballistics (drag + gravity, range table built at load), visible shells,
// salvo dispersion, turret train/elevation with firing arcs, and the same
// shell simulation carries the enemy's incoming rounds.

import * as THREE from "three";
import { SHELL_V0, SHELL_DRAG, GRAVITY, SALVO_RELOAD, TURRET_TRAIN_RATE, TURRET_ELEV_RATE } from "./const.js";
import { muzzleBlast, shellSplash, groundBurst } from "./effects.js";

const wrap = (a) => Math.atan2(Math.sin(a), Math.cos(a));

// ---------------------------------------------------------------------------
// Range table: integrate the trajectory once per elevation at load time.
// `fall` is the angle of fall at sea-level impact — the plot needs it to
// shoot at targets that sit above the waterline (a battery 900 m up a
// mountain is hit by aiming long: height / tan(fall) further downrange).
const TABLE = []; // {elev(rad), range, tof, fall(rad)}
for (let deg = 1; deg <= 45; deg += 0.5) {
  const e = deg * Math.PI / 180;
  let x = 0, y = 12, vx = Math.cos(e) * SHELL_V0, vy = Math.sin(e) * SHELL_V0, t = 0;
  const dt = 0.05;
  while (y > 0 && t < 120) {
    const v = Math.hypot(vx, vy);
    vx -= SHELL_DRAG * v * vx * dt;
    vy -= (SHELL_DRAG * v * vy + GRAVITY) * dt;
    x += vx * dt; y += vy * dt; t += dt;
  }
  TABLE.push({ elev: e, range: x, tof: t, fall: Math.atan2(-vy, vx) });
}
export const MAX_RANGE = TABLE[TABLE.length - 1].range;

export function solveElevation(range) {
  const last = TABLE[TABLE.length - 1];
  if (range >= MAX_RANGE) return { elev: last.elev, tof: last.tof, fall: last.fall, capped: true };
  for (let i = 1; i < TABLE.length; i++) {
    if (TABLE[i].range >= range) {
      const a = TABLE[i - 1], b = TABLE[i];
      const f = (range - a.range) / Math.max(1, b.range - a.range);
      return {
        elev: a.elev + (b.elev - a.elev) * f,
        tof: a.tof + (b.tof - a.tof) * f,
        fall: a.fall + (b.fall - a.fall) * f,
      };
    }
  }
  return { elev: TABLE[0].elev, tof: TABLE[0].tof, fall: TABLE[0].fall };
}

// Aim range that drops a shell on a target `height` meters above the sea at
// horizontal range `range`. The fall angle where the shell crosses the
// target's altitude is shallower than at sea level, so a closed-form offset
// lands short — integrate the actual trajectory and iterate. Cached: the
// solve reruns only when the plot's range or the target height moves.
const _h2r = { key: "", aim: 0 };
export function solveForHeight(range, height) {
  if (!height || height < 2) return range;
  const key = `${Math.round(range / 25)}:${Math.round(height / 10)}`;
  if (_h2r.key === key) return _h2r.aim;
  let aim = Math.min(MAX_RANGE - 50, range + height * 3);
  for (let it = 0; it < 5; it++) {
    const e = solveElevation(aim).elev;
    let x = 0, y = 12, vx = Math.cos(e) * SHELL_V0, vy = Math.sin(e) * SHELL_V0, t = 0, cross = -1;
    const dt = 0.05;
    while (y > 0 && t < 120) {
      const v = Math.hypot(vx, vy);
      vx -= SHELL_DRAG * v * vx * dt;
      vy -= (SHELL_DRAG * v * vy + GRAVITY) * dt;
      x += vx * dt; y += vy * dt; t += dt;
      if (vy < 0 && y <= height) { cross = x; break; }
    }
    if (cross < 0) { aim = Math.min(MAX_RANGE - 50, aim + 800); continue; } // never got that high: aim longer/steeper
    const err = range - cross;
    if (Math.abs(err) < 12) break;
    aim = Math.max(2000, Math.min(MAX_RANGE - 50, aim + err));
  }
  _h2r.key = key; _h2r.aim = aim;
  return aim;
}

// ---------------------------------------------------------------------------
export class Guns {
  constructor(G) {
    this.G = G; // {scene, shipGroup, shipInner, shipState, worldFx, audio, hud, enemies(), damage}
    this.turrets = G.ship.turrets;
    this.shells = [];
    this.shellMeshes = [];
    this.reload = 0;
    this.pool = new THREE.Group();
    G.scene.add(this.pool);
    this.shellGeo = new THREE.SphereGeometry(0.45, 6, 5);
    this.shellMat = new THREE.MeshBasicMaterial({ color: 0x2a2f34 });
    this.tracerMat = new THREE.MeshBasicMaterial({ color: 0xffc27d });
    this.order = null; // {bearing (world), range}
    this.trainNoise = false;
    this.localTurret = null; // turret under local control (index)
  }

  // World bearing/elevation order from fire control. bearing: compass rad.
  setOrder(order) { this.order = order; }

  update(dt) {
    const G = this.G;
    this.reload = Math.max(0, this.reload - dt);
    const H = G.shipState.heading;
    let anyMoving = false;

    for (let i = 0; i < this.turrets.length; i++) {
      const t = this.turrets[i];
      if (t.disabled) continue;
      let wantTrain = null, wantElev = t.orderElev;
      if (this.localTurret === i && t.localOrder) {
        wantTrain = t.localOrder.train; wantElev = t.localOrder.elev;
      } else if (this.order) {
        const tau = wrap(this.order.bearing - H);
        const sol = solveElevation(this.order.range);
        wantTrain = tau; wantElev = sol.elev;
      }
      if (wantTrain !== null) {
        // firing arc: fore turrets can't shoot abaft, aft can't shoot forward
        const rel = t.aft ? wrap(wantTrain - Math.PI) : wantTrain;
        const lim = 2.62; // +/- 150 deg
        t.blocked = Math.abs(rel) > lim;
        const clamped = Math.max(-lim, Math.min(lim, rel));
        const target = t.aft ? wrap(clamped + Math.PI) : clamped;
        const d = wrap(target - t.train);
        const step = Math.sign(d) * Math.min(Math.abs(d), TURRET_TRAIN_RATE * dt);
        if (Math.abs(d) > 0.001) { t.train = wrap(t.train + step); anyMoving = true; }
        const de = wantElev - t.elev;
        t.elev += Math.sign(de) * Math.min(Math.abs(de), TURRET_ELEV_RATE * dt);
        t.matched = Math.abs(d) < 0.008 && Math.abs(de) < 0.01 && !t.blocked;
      } else {
        t.matched = false;
      }
      t.group.rotation.y = -t.train;
      for (const b of t.barrels) b.rotation.z = t.elev;
      t.ready = t.matched && this.reload <= 0 && !t.disabled;
    }
    if (anyMoving !== this.trainNoise) {
      this.trainNoise = anyMoving;
      G.audio.turretTrain(anyMoving);
    }

    this.updateShells(dt);
  }

  readyTurrets() { return this.turrets.filter((t) => t.ready); }

  // Fire every ready turret. err: {bearing, range} solution error from FC.
  fireSalvo(err, opts = {}) {
    const G = this.G;
    const fired = [];
    for (let i = 0; i < this.turrets.length; i++) {
      const t = this.turrets[i];
      if (!t.ready) continue;
      if (opts.only !== undefined && opts.only !== i) continue;
      this.fireTurret(t, err, opts);
      fired.push(t.id);
    }
    if (fired.length) {
      this.reload = SALVO_RELOAD;
      G.audio.salvo();
      G.buzz?.([60, 40, 60]); // nine rifles, felt through the deck plates
      G.shipState.recoil = 0.02 * fired.length;
      return fired;
    }
    return null;
  }

  // Local-control shot: fly the barrels exactly as laid, no rangekeeper.
  fireLocal(t) {
    if (!t.ready) return false;
    const G = this.G;
    const H = G.shipState.heading;
    for (const b of t.barrels) {
      const m = new THREE.Vector3(t.muzzleLen, 0, 0);
      b.localToWorld(m);
      const bearing = H + t.train + randn() * 0.004;
      const elev = Math.max(0.01, t.elev + randn() * 0.003);
      const dir = new THREE.Vector3(Math.sin(bearing) * Math.cos(elev), Math.sin(elev), Math.cos(bearing) * Math.cos(elev));
      this.spawnShell(m, dir.multiplyScalar(SHELL_V0), { own: true, local: true });
      muzzleBlast(G.worldFx, m, new THREE.Vector3(Math.sin(bearing), 0.15, Math.cos(bearing)));
    }
    this.reload = SALVO_RELOAD;
    G.audio.salvo();
    G.buzz?.(70);
    return true;
  }

  fireTurret(t, err, opts = {}) {
    const G = this.G;
    const H = G.shipState.heading;
    const salvoId = (this._salvoId = (this._salvoId || 0) + 1);
    for (const b of t.barrels) {
      // muzzle world position
      const m = new THREE.Vector3(t.muzzleLen, 0, 0);
      b.localToWorld(m); // barrel pivot -> shipInner? barrels are children of turret group which is child of shipInner
      // bearing of this shot: turret train + heading + dispersion + error
      const bearing = H + t.train + (err?.bearing ?? 0) + randn() * 0.004;
      const range = Math.max(2000, (this.order?.range ?? 15000) + (err?.range ?? 0));
      const sol = solveElevation(range + randn() * range * 0.011);
      const dir = new THREE.Vector3(Math.sin(bearing) * Math.cos(sol.elev), Math.sin(sol.elev), Math.cos(bearing) * Math.cos(sol.elev));
      this.spawnShell(m, dir.multiplyScalar(SHELL_V0), { salvoId, own: true });
      muzzleBlast(G.worldFx, m, new THREE.Vector3(Math.sin(bearing), 0.15, Math.cos(bearing)));
    }
  }

  spawnShell(pos, vel, meta) {
    const mesh = new THREE.Mesh(this.shellGeo, meta.own ? this.tracerMat : this.shellMat);
    mesh.position.copy(pos);
    this.pool.add(mesh);
    this.shells.push({ pos: pos.clone(), vel: vel.clone(), mesh, t: 0, trail: 0, ...meta });
  }

  updateShells(dt) {
    const G = this.G;
    for (let i = this.shells.length - 1; i >= 0; i--) {
      const s = this.shells[i];
      // substep for stability at 800 m/s
      const steps = Math.max(2, Math.ceil(dt / 0.05));
      for (let k = 0; k < steps; k++) {
        const h = dt / steps;
        const v = s.vel.length();
        s.vel.x -= SHELL_DRAG * v * s.vel.x * h;
        s.vel.z -= SHELL_DRAG * v * s.vel.z * h;
        s.vel.y -= (SHELL_DRAG * v * s.vel.y + GRAVITY) * h;
        s.pos.addScaledVector(s.vel, h);
      }
      s.t += dt;
      s.trail -= dt;
      if (s.trail <= 0 && s.pos.y > 30) {
        s.trail = 0.5;
        G.worldFx.spawn(s.pos, { count: 1, speed: 0.4, life: 1.6, size: 2.2, jitter: 0.5,
          colors: [0xcfd6dd, 0xb8c0c8], drag: 0.5, grow: 2 });
      }
      s.mesh.position.copy(s.pos);
      // land rises above the waterline on the shore-bombardment missions;
      // only bursts on the way down count (shore batteries launch from height)
      const gh = G.terrain ? G.terrain.heightAt(s.pos.x, s.pos.z) : 0;
      if (s.pos.y <= gh && (s.vel.y < 0 || gh <= 0)) {
        s.pos.y = gh;
        this.impact(s, gh);
        this.pool.remove(s.mesh);
        this.shells.splice(i, 1);
        s.dead = true; // the salvo camera lingers on the burst
      }
    }
  }

  impact(s, gh = 0) {
    const G = this.G;
    const onLand = gh > 0.05;
    const at = new THREE.Vector3(s.pos.x, gh + 0.5, s.pos.z);
    if (s.own) {
      // did we hit anything — a ship, or a shore target inside the burst?
      let result = null;
      for (const e of G.targets()) {
        const r = e.tryHit(at);
        if (r) { result = { enemy: e, ...r }; break; }
      }
      if (onLand) {
        groundBurst(G.worldFx, at, true);
        if (G.terrain?.cityAt(at.x, at.z)) G.mission?.onCityHit?.(G);
      } else if (!result) shellSplash(G.worldFx, at, true);
      G.fc?.reportSplash(s, at, result);
      // impact audio only if camera near-ish
      const d = at.distanceTo(G.cameraWorld());
      if (d < 3500) G.audio.splash(true);
    } else {
      // enemy round arriving at us
      if (onLand) { groundBurst(G.worldFx, at, false); return; }
      G.damage.resolveIncoming(at, s);
    }
  }
}

export function randn() {
  return (Math.random() + Math.random() + Math.random() + Math.random() - 2) / 1.2;
}
